import type {Component, Editor} from 'grapesjs';
import PMWEmailEditorPlugin from '../src/index';
import PMWEmailsNamespace from '../src/var';

PMWEmailsNamespace();

interface DeviceConfig {
  id: string;
  name: string;
  width: string;
  widthMedia?: string;
  minWidthMedia?: string;
}

interface CanvasConfig {
  frameContent: string;
  styles: string[];
  customBadgeLabel: (component: Component) => string;
}

interface StyleManagerConfig {
  appendTo: string;
  sectors: unknown[];
}

interface KeymapsConfig {
  defaults: Record<string, unknown>;
}

interface RichTextEditorConfig {
  stylePrefix: string;
  adjustToolbar: number;
  actions: string[];
}

interface AssetManagerConfig {
  upload: string;
  uploadName: string;
  autoAdd: number;
}

interface StorageManagerConfig {
  type: string;
  autosave: boolean;
  stepsBeforeSave: number;
  onStore: (data: unknown, editor: Editor) => unknown;
  onLoad: (data: unknown, editor: Editor) => unknown;
}

interface PanelsConfig {
  defaults: unknown[];
}

interface SelectorManagerConfig {
  componentFirst: boolean;
}

interface DeviceManagerConfig {
  default: string;
  devices: DeviceConfig[];
}

interface UndoManagerConfig {
  trackSelection: number;
}

interface ParserConfig {
  optionsHtml: {
    allowScripts: boolean;
    allowUnsafeAttr: boolean;
  };
}

interface GrapesJSConfig {
  mode: string;
  avoidInlineStyle: boolean;
  forceClass: boolean;
  container: string;
  height: string;
  multipleSelection: boolean;
  cssIcons: null;
  noticeOnUnload: number;
  canvas: CanvasConfig;
  styleManager: StyleManagerConfig;
  keymaps: KeymapsConfig;
  richTextEditor: RichTextEditorConfig;
  assetManager: AssetManagerConfig;
  storageManager: StorageManagerConfig;
  panels: PanelsConfig;
  selectorManager: SelectorManagerConfig;
  deviceManager: DeviceManagerConfig;
  undoManager: UndoManagerConfig;
  plugins: unknown[];
  parser: ParserConfig;
}

const GRAPESJS_CONFIG: GrapesJSConfig = {
  mode: 'simple',
  avoidInlineStyle: false,
  forceClass: false,
  container: '#html-editor-canvas',
  height: '100%',
  multipleSelection: true,
  cssIcons: null,
  noticeOnUnload: 1,
  canvas: {
    frameContent: '',
    styles: [window.PMW.util.asset_url(`css/email-maker/editor-canvas.css?v=${window.PMW.VERSION}`)],
    customBadgeLabel: (component: Component): string => {
      const name = component.get('name');
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      return `<span class="toolbar-name"> ${typeof name === 'function' ? name() : name}</span>`;
    },
  },
  styleManager: {
    appendTo: '.html-editor-style-manager',
    sectors: [],
  },
  keymaps: {
    defaults: {},
  },
  richTextEditor: {
    stylePrefix: 'rte-',
    adjustToolbar: 1,
    actions: ['bold', 'underline', 'italic', 'strikethrough', 'link'],
  },
  assetManager: {
    upload: window.PMW.util.site_url('posterimage/emailUpload'),
    uploadName: 'files',
    autoAdd: 1,
  },
  storageManager: {
    type: 'remote-storage',
    autosave: false,
    stepsBeforeSave: 1,
    onStore: (data: unknown, editor: Editor) => {
      const storage = editor?.StorageManager?.getCurrentStorage();
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return,@typescript-eslint/no-unsafe-call
      return storage ? storage.onStore(data, editor) : undefined;
    },
    onLoad: (data: unknown, editor: Editor) => {
      const storage = editor?.StorageManager?.getCurrentStorage();
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return,@typescript-eslint/no-unsafe-call
      return storage ? storage.onLoad(data, editor) : undefined;
    },
  },
  panels: {
    defaults: [],
  },
  selectorManager: {
    componentFirst: true,
  },
  deviceManager: {
    default: window.PMW.emails.DEVICE_DESKTOP,
    devices: [
      {
        id: window.PMW.emails.DEVICE_DESKTOP,
        name: 'Desktop',
        width: '100%',
        minWidthMedia: '767px',
      },
      {
        id: window.PMW.emails.DEVICE_TABLET,
        name: 'Tablet',
        width: '600px',
        widthMedia: '768px',
        minWidthMedia: '551px',
      },
      {
        id: window.PMW.emails.DEVICE_MOBILE,
        name: 'Mobile',
        width: '345px',
        widthMedia: '550px',
      },
    ],
  },
  undoManager: {
    trackSelection: 0,
  },
  plugins: [PMWEmailEditorPlugin],
  parser: {
    optionsHtml: {
      allowScripts: false,
      allowUnsafeAttr: false,
    },
  },
};

export {GRAPESJS_CONFIG as default};